class ModalBase extends HTMLElement {
  get focusableSelectors() {
    return 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  }

  get modalTransition() {
    throw new Error('modalTransition getter must be implemented by subclass');
  }

  trapFocus() {
    const focusable = [...this.panel.querySelectorAll(this.focusableSelectors)];
    if (!focusable.length) return;

    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    // Focus the dialog itself, not the first focusable child — focusing a
    // child would paint a focus ring over it and read as a selection state.
    this.panel.focus();
    this.removeTrapFocus();

    this.focusTrapHandler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };
    this.panel.addEventListener('keydown', this.focusTrapHandler);
  }

  removeTrapFocus() {
    if (this.focusTrapHandler && this.panel) {
      this.panel.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  openModal(beforeOpen) {
    this.previousActiveElement = document.activeElement;
    this.hidden = false;
    this.isOpen = true;

    beforeOpen?.();

    const { overlayOpenClasses, overlayClosedClasses, panelOpenClasses, panelClosedClasses } = this.modalTransition;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.overlay?.classList.remove(...overlayClosedClasses);
        this.overlay?.classList.add(...overlayOpenClasses);
        this.panel?.classList.remove(...panelClosedClasses);
        this.panel?.classList.add(...panelOpenClasses);
        document.body.classList.add('overflow-hidden');
        this.setAttribute('aria-hidden', 'false');
        this.trapFocus();
      });
    });
  }

  closeModal(afterClose) {
    this.isOpen = false;
    this.removeTrapFocus();

    const { overlayOpenClasses, overlayClosedClasses, panelOpenClasses, panelClosedClasses, transitionDuration } =
      this.modalTransition;

    this.overlay?.classList.remove(...overlayOpenClasses);
    this.overlay?.classList.add(...overlayClosedClasses);
    this.panel?.classList.remove(...panelOpenClasses);
    this.panel?.classList.add(...panelClosedClasses);
    document.body.classList.remove('overflow-hidden');
    this.setAttribute('aria-hidden', 'true');

    setTimeout(() => {
      this.hidden = true;
      this.previousActiveElement?.focus();
      afterClose?.();
    }, transitionDuration);
  }
}

window.ModalBase = ModalBase;
